import { AVAILABILITY, LISTING_KINDS, productLifecycle } from "@platform/domain";
import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Money } from "../common/money.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
/** Catalog shapes shared by the seller, operator, and public routes (cards 034 to 041). */
/** The listing's limits, which the API checks and the seller's screens keep (card 043). */
export { LISTING_TEXT, MAX_MEDIA, MAX_SKUS, MAX_TIERS } from "@platform/domain";
export const ListingKind = z.enum(LISTING_KINDS).meta({
    id: "ListingKind",
    description: "Set per category branch: goods, or services such as valve testing",
});
export const Availability = z.enum(AVAILABILITY).meta({
    id: "Availability",
    description: "Goods: in stock, made to order, or unavailable; a service: available or not",
});
export const ProductStatus = z.enum(productLifecycle.states).meta({ id: "ProductStatus" });
const AttributeKey = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/);
const attributeCommon = {
    key: AttributeKey,
    label: LocalizedText,
    required: z.boolean(),
    filterable: z.boolean().describe("Offered as a search filter"),
};
export const AttributeDefinition = z
    .discriminatedUnion("type", [
    z.object({ ...attributeCommon, type: z.literal("text"), maxLength: z.number().int().min(1).max(500) }),
    z.object({
        ...attributeCommon,
        type: z.literal("number"),
        unit: z.string().min(1).max(20).nullable(),
        min: z.number().nullable(),
        max: z.number().nullable(),
        integer: z.boolean(),
    }),
    z.object({
        ...attributeCommon,
        type: z.literal("choice"),
        multiple: z.boolean(),
        options: z
            .array(z.object({ value: AttributeKey, label: LocalizedText }))
            .min(2)
            .max(100),
    }),
    z.object({ ...attributeCommon, type: z.literal("boolean") }),
])
    .meta({ id: "AttributeDefinition" });
export const AttributeSchema = z
    .object({ version: z.number().int().positive(), attributes: z.array(AttributeDefinition).max(40) })
    .meta({
    id: "AttributeSchema",
    description: "A leaf category's attributes; the platform's own format (card 034)",
});
export const AttributeValues = z
    .record(AttributeKey, z.union([z.string().max(500), z.number(), z.boolean(), z.array(AttributeKey).max(100)]))
    .meta({ id: "AttributeValues" });
export const PriceTier = z
    .object({ minQuantity: Quantity, unitPrice: Money })
    .meta({ id: "PriceTier", description: "The unit price from a quantity on; tiers start at the SKU's MOQ" });
export const Sku = z
    .object({
    code: z.string().trim().min(1).max(64),
    /** What distinguishes this SKU from the product's others, such as a size. */
    options: z.record(AttributeKey, z.string().max(100)),
    moq: Quantity,
    unit: z.string().trim().min(1).max(20),
    tiers: z.array(PriceTier).max(10),
})
    .meta({ id: "Sku" });
export const ProductMedia = z
    .object({ fileId: Id, alt: LocalizedText.nullable() })
    .meta({ id: "ProductMedia" });
/** What a seller writes; a draft may be incomplete, and submission checks it (card 038). */
export const ProductDraft = z
    .object({
    categoryId: Id,
    name: LocalizedText,
    description: LocalizedText.nullable(),
    attributes: AttributeValues,
    hsCode: z
        .string()
        .regex(/^\d{6,10}$/)
        .nullable()
        .describe("Goods only"),
    originCountry: CountryCode.nullable().describe("Goods only"),
    serviceRegions: z
        .array(CountryCode)
        .max(250)
        .nullable()
        .describe("Services only: where the service is offered"),
    leadTimeDays: z.number().int().min(0).max(730).nullable().describe("For a service, the turnaround"),
    availability: Availability,
    availableQuantity: Quantity.nullable().describe("Goods only"),
    sample: Money.nullable().describe("Goods only: the price of a sample, ordered with an MOQ of 1"),
    inquiryOnly: z.boolean(),
    skus: z.array(Sku).max(50),
    media: z.array(ProductMedia).max(12),
})
    .meta({ id: "ProductDraft" });
export const ProductRevision = z
    .object({ id: Id, content: ProductDraft, createdAt: Timestamp, updatedAt: Timestamp })
    .meta({
    id: "ProductRevision",
    description: "Edits of a published product, waiting to be submitted; public pages keep showing the product",
});
export const ProductReviewSummary = z
    .object({
    id: Id,
    revision: z.boolean().describe("Whether operators review edits of the published product"),
    state: z.enum(["open", "approved", "rejected"]),
    reason: z.string().nullable().describe("The operator's reason, once decided"),
    createdAt: Timestamp,
    decidedAt: Timestamp.nullable(),
})
    .meta({
    id: "ProductReviewSummary",
    description: "An operators' review; which list matched is not shown to the seller",
});
export const ProductBlock = z
    .object({ reason: z.string(), at: Timestamp })
    .meta({ id: "ProductBlock", description: "Why and when operators blocked the product" });
export const ProductView = ProductDraft.extend({
    id: Id,
    shopId: Id,
    listingKind: ListingKind,
    slug: z.string().nullable().describe("Set at first publication and never changed"),
    status: ProductStatus,
    attributeSchemaVersion: z
        .number()
        .int()
        .nonnegative()
        .describe("The leaf's schema version the attributes were last checked against"),
    pendingRevision: ProductRevision.nullable(),
    review: ProductReviewSummary.nullable().describe("The latest review of the product or of its edits"),
    block: ProductBlock.nullable(),
    version: z.number().int().positive(),
    publishedAt: Timestamp.nullable(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
}).meta({ id: "ProductView" });
export const ProductSummary = z
    .object({
    id: Id,
    categoryId: Id,
    listingKind: ListingKind,
    name: LocalizedText,
    slug: z.string().nullable(),
    status: ProductStatus,
    inquiryOnly: z.boolean(),
    coverFileId: Id.nullable().describe("The first image"),
    pendingRevision: z.boolean(),
    version: z.number().int().positive(),
    updatedAt: Timestamp,
})
    .meta({ id: "ProductSummary" });
export const ShopStatus = z.enum(["draft", "active", "suspended", "closed"]).meta({ id: "ShopStatus" });
export const SellingStatus = z.enum(["not_ready", "can_sell", "suspended"]).meta({
    id: "SellingStatus",
    description: "Follows the shop's bank account while trade is paid offline (ADR 0021), otherwise its payout account (ADR 0017)",
});
export const ShopView = z
    .object({
    id: Id,
    slug: z.string(),
    name: LocalizedText,
    description: LocalizedText.nullable(),
    logoFileId: Id.nullable(),
    qualifications: z.array(z.object({ fileId: Id, title: LocalizedText })).max(20),
    serviceRegions: z.array(CountryCode).max(250),
    status: ShopStatus,
    sellingStatus: SellingStatus,
    openedAt: Timestamp,
    version: z.number().int().positive(),
})
    .meta({ id: "ShopView" });
export const CategoryView = z
    .object({
    id: Id,
    parentId: Id.nullable(),
    slug: z.string(),
    name: LocalizedText,
    listingKind: ListingKind,
    isLeaf: z.boolean(),
    sortOrder: z.number().int(),
    attributeSchema: AttributeSchema.nullable(),
})
    .meta({ id: "CategoryView" });
//# sourceMappingURL=listing.js.map